"use client";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import type { AppointmentForm } from "@/lib/hooks/useAppointment";
import {
  getCanonicalBusinessForStylist,
  resolveBusinessForAppointment,
} from "@/lib/api/openingsLocations";
import { getEditionUserPathIdFromMemberId } from "@/consts/editionStudio";

import { useBookingApi, type BookingApi } from "./bookingApi";

/**
 * True only when the booked business can plausibly disagree in price with the
 * stylist's canonical menu — i.e. the slot books somewhere other than the
 * business that menu came from. That's the only case a re-price call can
 * change anything, so this gates the network round-trip out of the common
 * single-business path. Either side being unresolvable (an unmapped memberId,
 * an appointment whose identifiers resolve to nothing) fails toward
 * re-pricing rather than trusting a price this check can't vouch for.
 *
 * The booked business comes from the whole appointment CONTEXT, not from
 * `appointment.scheduleId` alone: ruling R11 established that `scheduleId` is
 * the MEMBER'S own schedule id on both booking surfaces and never a registry
 * schedule id, so a schedule-keyed lookup resolved nothing on 100% of
 * bookings and this gate opened every time. That cost every single-business
 * Eddie's booking two extra round-trips against an API that rate-limits
 * per-IP on shared Vercel egress (see openingsClient.ts).
 */
const canDisagreeWithCanonicalMenu = (appointment: AppointmentForm) => {
  const bookedBusiness = resolveBusinessForAppointment(appointment);
  const pathId = getEditionUserPathIdFromMemberId(appointment.userId);
  const canonicalBusiness = pathId
    ? getCanonicalBusinessForStylist(pathId)
    : undefined;
  if (!bookedBusiness || !canonicalBusiness) return true;
  return bookedBusiness !== canonicalBusiness;
};

export type ServicePricingStatus = "ready" | "pending" | "error";

export type RepricedServicesState = {
  services: AppointmentForm["services"];
  status: ServicePricingStatus;
  /** True when the booked slot belongs to a different/unknown business. */
  requiresAuthoritativePricing: boolean;
  error: Error | null;
  retry: () => void;
};

type SettledPricingState = {
  appointment: AppointmentForm;
  bookingApi: BookingApi;
  attempt: number;
  status: "ready" | "error";
  services: AppointmentForm["services"];
  error: Error | null;
};

/**
 * One controller is mounted by `NewAppointmentModal` and shared through the
 * context below. Keeping the request here, above every route component, means
 * review, phone routing, 428 recovery and payment all observe the exact same
 * result instead of issuing independent requests that can settle differently.
 *
 * Cross-business pricing is authoritative: until the request succeeds the
 * state is `pending`, and a rejection becomes an explicit retryable `error`.
 * Canonical services remain in `services` only so non-price appointment data
 * can render; consumers must not make a payment decision unless `status` is
 * `ready`. Single-business bookings are ready synchronously and make no call.
 */
export const useRepricedServicesController = (
  appointment: AppointmentForm | undefined,
): RepricedServicesState => {
  const bookingApi = useBookingApi();
  const [attempt, setAttempt] = useState(0);
  const [settled, setSettled] = useState<SettledPricingState>();
  const requiresAuthoritativePricing = appointment
    ? canDisagreeWithCanonicalMenu(appointment)
    : false;

  const retry = useCallback(() => setAttempt((value) => value + 1), []);

  useEffect(() => {
    if (!appointment || !requiresAuthoritativePricing) return;

    let cancelled = false;
    bookingApi
      .getServicePricing(
        appointment.scheduleId,
        appointment.userId,
        appointment.services,
      )
      .then((services) => {
        if (cancelled) return;
        setSettled({
          appointment,
          bookingApi,
          attempt,
          status: "ready",
          services,
          error: null,
        });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setSettled({
          appointment,
          bookingApi,
          attempt,
          status: "error",
          services: appointment.services,
          error:
            error instanceof Error
              ? error
              : new Error("Unable to confirm current service pricing."),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [
    appointment,
    attempt,
    bookingApi,
    requiresAuthoritativePricing,
  ]);

  // This is the modal-level context value: memoized (per the repo's context
  // rule) so unrelated modal re-renders — route transitions, breakpoint
  // changes — don't hand every pricing consumer a fresh identity.
  return useMemo((): RepricedServicesState => {
    if (!appointment) {
      return {
        services: [],
        status: "ready",
        requiresAuthoritativePricing: false,
        error: null,
        retry,
      };
    }

    if (!requiresAuthoritativePricing) {
      return {
        services: appointment.services,
        status: "ready",
        requiresAuthoritativePricing: false,
        error: null,
        retry,
      };
    }

    const isCurrentResult =
      settled?.appointment === appointment &&
      settled.bookingApi === bookingApi &&
      settled.attempt === attempt;

    if (!isCurrentResult) {
      return {
        services: appointment.services,
        status: "pending",
        requiresAuthoritativePricing: true,
        error: null,
        retry,
      };
    }

    return {
      services: settled.services,
      status: settled.status,
      requiresAuthoritativePricing: true,
      error: settled.error,
      retry,
    };
  }, [
    appointment,
    requiresAuthoritativePricing,
    settled,
    attempt,
    bookingApi,
    retry,
  ]);
};

const RepricedServicesContext = createContext<RepricedServicesState | null>(
  null,
);

export const RepricedServicesProvider = RepricedServicesContext.Provider;

/**
 * Consume the single modal-level pricing state. This intentionally has no
 * local fallback: mounting a consumer without the provider would recreate the
 * duplicate-request bug this context exists to prevent, so fail loudly in
 * development and tests instead.
 */
export const useRepricedServices = (): RepricedServicesState => {
  const state = useContext(RepricedServicesContext);
  if (!state) {
    throw new Error(
      "useRepricedServices must be used within RepricedServicesProvider",
    );
  }
  return state;
};
